import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getProspects, deleteProspect, getSettings, getUsers, bulkProspects, API_URL } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fuzzyMatch } from '../components/DocumentPreview';
import { BulkActionBar, SelectCheckbox } from '../components/BulkActionBar';
import { Users as UsersIcon, Tag, Edit3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Search, 
    Plus, 
    Filter, 
    MoreHorizontal, 
    Phone, 
    Mail, 
    MapPin,
    Eye,
    Pencil,
    Trash2,
    UserCheck,
    UserX,
    X,
    User,
    Download
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export const ProspectsPage = () => {
    const { user } = useAuth();
    const canEdit = user?.role === 'admin' || user?.role === 'manager';
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [clientFilter, setClientFilter] = useState('all');
    const [commercialFilter, setCommercialFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sources, setSources] = useState([]);
    const [commerciaux, setCommerciaux] = useState([]);
    const [deleteId, setDeleteId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    useEffect(() => {
        const isClientParam = searchParams.get('is_client');
        if (isClientParam !== null) {
            setClientFilter(isClientParam);
        }
        const commercialParam = searchParams.get('commercial_id');
        if (commercialParam) {
            setCommercialFilter(commercialParam);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchProspects();
        fetchSettings();
        fetchCommerciaux();
    }, []);
    const fetchSettings = async () => {
        try {
            const res = await getSettings();
            setSources(res.data.sources_lead || []);
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const fetchCommerciaux = async () => {
        try {
            const res = await getUsers();
            setCommerciaux(res.data.filter(u => ['commercial', 'manager', 'admin'].includes(u.role)));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchProspects = async () => {
        try {
            const res = await getProspects({ exclude_syndics: true });
            setProspects(res.data);
        } catch (error) {
            console.error('Error fetching prospects:', error);
            toast.error('Erreur lors du chargement des prospects');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteProspect(deleteId);
            setProspects(prospects.filter(p => p.id !== deleteId));
            toast.success('Prospect supprimé');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
        setDeleteId(null);
    };

    const filteredProspects = prospects.filter(p => {
        const matchesSearch = search === '' || fuzzyMatch(
            [p.prenom, p.nom, p.email, p.telephone, p.societe, p.adresse, p.ville, p.code_postal]
                .filter(Boolean).join(' '),
            search
        );
        
        const matchesSource = sourceFilter === 'all' || p.source_lead === sourceFilter;
        const matchesClient = clientFilter === 'all' || 
            (clientFilter === 'true' && p.is_client) || 
            (clientFilter === 'false' && !p.is_client);
        const matchesCommercial = commercialFilter === 'all' || p.commercial_id === commercialFilter;
        const matchesType = typeFilter === 'all' || p.type_contact === typeFilter;
        
        return matchesSearch && matchesSource && matchesClient && matchesCommercial && matchesType;
    });

    const handleBulkApply = async ({ actionId, payload }) => {
        const fieldMap = { assign: 'commercial_id', source: 'source_lead', status: 'is_client' };
        const field = fieldMap[actionId];
        if (!field) throw new Error('Action inconnue');
        await bulkProspects('update', selectedIds, { [field]: payload[field] });
        setSelectedIds([]);
        await fetchProspects();
    };

    const handleBulkDelete = async () => {
        await bulkProspects('delete', selectedIds);
        setSelectedIds([]);
        await fetchProspects();
    };

    const toggleSelect = (id, checked) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    };

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? filteredProspects.map(p => p.id) : []);
    };

    return (
        <div className="space-y-4" data-testid="prospects-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight">Prospects & Clients</h1>
                    <p className="text-sm text-[#52525B] mt-1">{filteredProspects.length} contact(s)</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    {user?.role === 'admin' && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                window.open(`${API_URL}/api/exports/prospects`, '_blank');
                            }}
                            className="rounded-sm w-full sm:w-auto"
                            data-testid="export-prospects-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Exporter Excel
                        </Button>
                    )}
                    <Link to="/prospects/new" className="w-full sm:w-auto">
                        <Button className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white w-full sm:w-auto" data-testid="new-prospect-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau prospect
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-[#E4E4E7] p-3 sm:p-4">
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                        <Input
                            placeholder="Rechercher par nom, email, téléphone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 h-11 rounded-sm border-[#E4E4E7]"
                            data-testid="search-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                        <Select value={clientFilter} onValueChange={setClientFilter}>
                            <SelectTrigger className="sm:w-[160px] h-11 rounded-sm" data-testid="client-filter">
                                <SelectValue placeholder="Tous les contacts" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les contacts</SelectItem>
                                <SelectItem value="false">Prospects</SelectItem>
                                <SelectItem value="true">Clients</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={sourceFilter} onValueChange={setSourceFilter}>
                            <SelectTrigger className="sm:w-[180px] h-11 rounded-sm" data-testid="source-filter">
                                <SelectValue placeholder="Toutes les sources" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les sources</SelectItem>
                                {sources.map(source => (
                                    <SelectItem key={source} value={source}>{source}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="sm:w-[180px] h-11 rounded-sm" data-testid="type-filter">
                                <SelectValue placeholder="Toutes typologies" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes typologies</SelectItem>
                                <SelectItem value="particulier">Particuliers</SelectItem>
                                <SelectItem value="professionnel">Professionnels</SelectItem>
                                <SelectItem value="sci">SCI</SelectItem>
                            </SelectContent>
                        </Select>
                        {(user?.role === 'admin' || user?.role === 'manager') && (
                            <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                                <SelectTrigger className="sm:w-[180px] h-11 rounded-sm" data-testid="commercial-filter">
                                    <SelectValue placeholder="Tous les commerciaux" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les commerciaux</SelectItem>
                                    {commerciaux.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {(search || sourceFilter !== 'all' || clientFilter !== 'all' || commercialFilter !== 'all' || typeFilter !== 'all') && (
                            <Button
                                variant="outline"
                                onClick={() => { setSearch(''); setSourceFilter('all'); setClientFilter('all'); setCommercialFilter('all'); setTypeFilter('all'); }}
                                className="h-11 rounded-sm col-span-2 sm:col-span-1"
                                data-testid="clear-filters"
                            >
                                <X className="w-4 h-4 mr-1" /> Effacer
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bulk action bar (admin/manager only) */}
            {canEdit && (
                <BulkActionBar
                    selectedIds={selectedIds}
                    entityLabel="prospect(s)"
                    onClear={() => setSelectedIds([])}
                    onApply={handleBulkApply}
                    onDelete={handleBulkDelete}
                    actions={[
                        {
                            id: 'assign',
                            label: 'Réassigner',
                            icon: <UsersIcon className="w-4 h-4 mr-1" />,
                            dialogFields: [{
                                name: 'commercial_id',
                                label: 'Commercial',
                                type: 'select',
                                required: true,
                                options: commerciaux.map(c => ({ value: c.id, label: c.name }))
                            }]
                        },
                        {
                            id: 'source',
                            label: 'Changer source',
                            icon: <Tag className="w-4 h-4 mr-1" />,
                            dialogFields: [{
                                name: 'source_lead',
                                label: 'Source',
                                type: 'select',
                                required: true,
                                options: sources.map(s => ({ value: s, label: s }))
                            }]
                        },
                        {
                            id: 'status',
                            label: 'Changer statut',
                            icon: <Edit3 className="w-4 h-4 mr-1" />,
                            dialogFields: [{
                                name: 'is_client',
                                label: 'Statut',
                                type: 'select',
                                required: true,
                                options: [
                                    { value: 'false', label: 'Prospect' },
                                    { value: 'true', label: 'Client' }
                                ]
                            }]
                        }
                    ]}
                />
            )}

            {/* Table */}
            <div className="bg-white border border-[#E4E4E7]">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : filteredProspects.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-[#52525B]">Aucun prospect trouvé</p>
                        <Link to="/prospects/new" className="text-[#E85A24] hover:underline mt-2 inline-block">
                            Créer un nouveau prospect
                        </Link>
                    </div>
                ) : (
                    <>
                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-[#E4E4E7]">
                        {filteredProspects.map((prospect) => (
                            <div key={prospect.id} className="p-3" data-testid={`prospect-card-${prospect.id}`}>
                                <div className="flex items-start gap-3">
                                    {canEdit && (
                                        <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                                            <SelectCheckbox
                                                checked={selectedIds.includes(prospect.id)}
                                                onChange={(checked) => toggleSelect(prospect.id, checked)}
                                                dataTestid={`select-prospect-${prospect.id}`}
                                            />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => navigate(`/prospects/${prospect.id}`)}
                                        className="flex-1 min-w-0 text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0 ${prospect.is_client ? 'bg-[#16A34A]' : 'bg-[#E85A24]'}`}>
                                                {prospect.prenom?.charAt(0)}{prospect.nom?.charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-[#0A0A0A] truncate">{prospect.prenom} {prospect.nom}</p>
                                                {prospect.societe && <p className="text-xs text-[#52525B] truncate">{prospect.societe}</p>}
                                            </div>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-sm shrink-0 ${
                                                prospect.is_client
                                                    ? 'bg-[#16A34A]/10 text-[#16A34A]'
                                                    : 'bg-[#E85A24]/10 text-[#E85A24]'
                                            }`}>
                                                {prospect.is_client ? 'Client' : 'Prospect'}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#52525B]">
                                            {prospect.telephone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {prospect.telephone}</span>}
                                            {prospect.email && <span className="flex items-center gap-1 truncate max-w-[180px]"><Mail className="w-3 h-3" /> {prospect.email}</span>}
                                            {prospect.commercial_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {prospect.commercial_name}</span>}
                                            {prospect.source_lead && <span>· {prospect.source_lead}</span>}
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#F4F4F5]">
                                {canEdit && (
                                    <TableHead className="w-10">
                                        <SelectCheckbox
                                            checked={selectedIds.length > 0 && selectedIds.length === filteredProspects.length}
                                            onChange={toggleSelectAll}
                                            dataTestid="select-all-prospects"
                                        />
                                    </TableHead>
                                )}
                                <TableHead className="font-bold text-[#0A0A0A]">Contact</TableHead>
                                <TableHead className="font-bold text-[#0A0A0A]">Coordonnées</TableHead>
                                <TableHead className="font-bold text-[#0A0A0A]">Commercial</TableHead>
                                <TableHead className="font-bold text-[#0A0A0A]">Adresse</TableHead>
                                <TableHead className="font-bold text-[#0A0A0A]">Statut</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProspects.map((prospect) => (
                                <TableRow 
                                    key={prospect.id} 
                                    className="hover:bg-[#FAFAFA] cursor-pointer"
                                    onClick={() => navigate(`/prospects/${prospect.id}`)}
                                    data-testid={`prospect-row-${prospect.id}`}
                                >
                                    {canEdit && (
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <SelectCheckbox
                                                checked={selectedIds.includes(prospect.id)}
                                                onChange={(checked) => toggleSelect(prospect.id, checked)}
                                                dataTestid={`select-prospect-${prospect.id}`}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${prospect.is_client ? 'bg-[#16A34A]' : 'bg-[#E85A24]'}`}>
                                                {prospect.prenom?.charAt(0)}{prospect.nom?.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-medium text-[#0A0A0A]">{prospect.prenom} {prospect.nom}</p>
                                                {prospect.societe && <p className="text-sm text-[#52525B]">{prospect.societe}</p>}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="space-y-1">
                                            {prospect.telephone && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Phone className="w-4 h-4 text-[#52525B]" />
                                                    <span>{prospect.telephone}</span>
                                                </div>
                                            )}
                                            {prospect.email && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Mail className="w-4 h-4 text-[#52525B]" />
                                                    <span className="truncate max-w-[200px]">{prospect.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {prospect.commercial_name ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-[#7C3AED] flex items-center justify-center text-white text-xs font-bold">
                                                    {prospect.commercial_name.charAt(0)}
                                                </div>
                                                <span className="text-sm font-medium">{prospect.commercial_name}</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-[#DC2626] flex items-center gap-1">
                                                <User className="w-4 h-4" /> Non assigné
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(prospect.adresse || prospect.ville || prospect.code_postal) ? (
                                            <div className="text-sm text-[#52525B] max-w-[260px]">
                                                {prospect.adresse && <p className="truncate">{prospect.adresse}</p>}
                                                {(prospect.code_postal || prospect.ville) && (
                                                    <p className="truncate text-xs">
                                                        {prospect.code_postal} {prospect.ville}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-sm text-[#A1A1AA]">-</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-sm ${
                                            prospect.is_client 
                                                ? 'bg-[#16A34A]/10 text-[#16A34A]' 
                                                : 'bg-[#E85A24]/10 text-[#E85A24]'
                                        }`}>
                                            {prospect.is_client ? (
                                                <><UserCheck className="w-3 h-3" /> Client</>
                                            ) : (
                                                <><UserX className="w-3 h-3" /> Prospect</>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" data-testid={`prospect-menu-${prospect.id}`}>
                                                    <MoreHorizontal className="w-5 h-5" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => navigate(`/prospects/${prospect.id}`)}>
                                                    <Eye className="w-4 h-4 mr-2" /> Voir
                                                </DropdownMenuItem>
                                                {canEdit && (
                                                    <DropdownMenuItem onClick={() => navigate(`/prospects/${prospect.id}/edit`)}>
                                                        <Pencil className="w-4 h-4 mr-2" /> Modifier
                                                    </DropdownMenuItem>
                                                )}
                                                {canEdit && (
                                                    <DropdownMenuItem 
                                                        onClick={() => setDeleteId(prospect.id)}
                                                        className="text-[#DC2626]"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </div>
                    </>
                )}
            </div>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Le prospect sera déplacé dans la <strong>Corbeille</strong>. Vous pourrez le restaurer pendant <strong>30 jours</strong> depuis <code>/corbeille</code>. Ensuite il sera supprimé définitivement.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-[#DC2626] hover:bg-[#DC2626]/90"
                            data-testid="confirm-delete"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
